import mixpanel from 'mixpanel-browser';
import type {Config} from 'mixpanel-browser';

import {SESSION_PROVIDER_TAG} from '#lit/components/providers/session_provider/index.js';
import {sessionStore} from '#stores/session/index.js';
import type {NodeEnvironment} from '#types/environment.js';
import type {Nullable} from '#types/general.js';
import type {User} from '#types/user.js';
import {getCurrentEnv} from '#utils/environment.js';

export type EventProperties = Record<string, unknown>;

export type UniversalProperties = {
  organizationId?: number;
  organizationName?: string;
  teamId?: number;
  userId?: number;
  mixpanelProjectId?: string;
};

const NON_PRODUCTION_TENANTS = ['local', 'dev', 'staging', 'review'];
let didInitializeMixpanelFromSession = false;

// Determine the Mixpanel env based on the tenant
// Fallback to the node env (development or production - deployed envs)
function getMixpanelEnv(): NodeEnvironment {
  if (typeof window !== 'undefined' && window.GL_TENANT !== undefined) {
    const tenant = String(window.GL_TENANT).trim().toLowerCase();
    return NON_PRODUCTION_TENANTS.includes(tenant)
      ? 'development'
      : 'production';
  }

  return getCurrentEnv();
}

const ENV_SPECIFIC_TOKENS: Record<NodeEnvironment, string> = {
  development: '99165600dffa4ee9e779340d44af993e',
  production: 'b26259dc4cdbe3e36028ac0576182373', // deployed environments
};

const ENV_SPECIFIC_CONFIGS: Record<NodeEnvironment, Partial<Config>> = {
  development: {
    autocapture: false,
  },
  production: {
    autocapture: {
      click: false,
      dead_click: false,
      input: false,
      rage_click: false,
      scroll: false,
      submit: false,
    },
    secure_cookie: true,
  },
};

const isMixpanelEnabled = () => {
  return sessionStore.getState().isFlagEnabled('frontendMixpanelAnalytics');
};

export const initMixpanel = (config: Partial<Config> = {}) => {
  if (!isMixpanelEnabled()) return;

  const env = getMixpanelEnv();
  try {
    mixpanel.init(ENV_SPECIFIC_TOKENS[env], {
      ...ENV_SPECIFIC_CONFIGS[env],
      ...config,
    });
  } catch (error) {
    console.error('Error initializing Mixpanel', error);
  }
};

const identifyUser = (
  user: Nullable<User>,
  mixpanelDistinctId: Nullable<string>
) => {
  if (!user || !mixpanelDistinctId) {
    console.warn(
      'User or distinct ID not found; stopping mixpanel identification.'
    );
    return;
  }

  mixpanel.identify(mixpanelDistinctId);
  mixpanel.people.set({
    $name: user.name,
    $email: user.email,
  });
};

const registerUniversalProperties = ({
  mixpanelProjectId,
  organizationId,
  organizationName,
  teamId,
  userId,
}: UniversalProperties) => {
  if (!isMixpanelEnabled()) return;

  mixpanel.register({
    'Organization ID': organizationId,
    'Organization name': organizationName,
    'Team ID': teamId,
    'User ID': userId,
    env: window.GL_TENANT, // tenant env
    'Project ID': mixpanelProjectId,
  });
};

export const resetMixpanelUser = () => {
  if (!isMixpanelEnabled()) return;

  mixpanel.reset();
};

export const trackMixpanelEvent = (
  eventName: string,
  properties?: EventProperties
) => {
  if (!isMixpanelEnabled()) return;

  mixpanel.track(eventName, properties);
};

export const initMixpanelFromSession = (): void => {
  if (!isMixpanelEnabled()) return;

  initMixpanel();

  const {user, org, team, mixpanelProjectId, mixpanelDistinctId} =
    sessionStore.getState();
  if (!user || !org || !mixpanelProjectId || !mixpanelDistinctId) return;

  identifyUser(user, mixpanelDistinctId);
  registerUniversalProperties({
    userId: user.id,
    organizationId: org.id,
    organizationName: org.name,
    teamId: team?.id,
    mixpanelProjectId: mixpanelProjectId ?? undefined,
  });
};

const initializeMixpanelFromSessionOnce = () => {
  if (didInitializeMixpanelFromSession) return;
  didInitializeMixpanelFromSession = true;
  initMixpanelFromSession();
};

export const initMixpanelFromSessionWhenReady = (): void => {
  if (Object.keys(sessionStore.getState().flags).length > 0) {
    initializeMixpanelFromSessionOnce();
    return;
  }

  document
    .querySelector(SESSION_PROVIDER_TAG)
    ?.addEventListener('rendered', initializeMixpanelFromSessionOnce, {
      once: true,
    });
};
